/**
 * Feed the Billing & Payables AP queue from supplier quote awards.
 *
 * Best-effort: bill tracking must never block the award flow, so failures
 * are logged and swallowed.
 */

import type { ProjectPLBucket, RfqProject } from '../types';
import type { SupplierBill } from './supplierBills';
import {
  createSupplierBill,
  findSupplierBillForCncOrgAward,
  findSupplierBillForRfqAward,
  updateSupplierBillDetails,
  voidSupplierBillForRfqAward,
  voidSupplierBillsForRfqOrg,
} from './supplierBillsService';

/** Org-level bill key used when a CNC award has no quote submission id. */
export function cncRfqAwardSubmissionKey(orgId: string): string {
  return `org:${orgId}`;
}

function isOrgAwardKey(submissionKey: string, orgId: string): boolean {
  const key = submissionKey.trim();
  return key === cncRfqAwardSubmissionKey(orgId) || key.startsWith('org:');
}

function sameCncAwardAmount(
  existingAmount: number | null | undefined,
  nextAmount: number | null | undefined,
): boolean {
  return (
    Math.round((Number(existingAmount) || 0) * 100)
    === Math.round((Number(nextAmount) || 0) * 100)
  );
}

/**
 * Reuse an RFQ+org CNC bill when this write is the org-key path, or when a
 * server/client race already stored the same award dollars under another key.
 * Different-amount structure quotes still create their own bill.
 */
export function shouldReuseCncOrgAwardBill(args: {
  submissionKey: string;
  orgId: string;
  amount: number | null | undefined;
  existing: Pick<SupplierBill, 'amount'> | null | undefined;
}): boolean {
  if (!args.existing) return false;
  if (isOrgAwardKey(args.submissionKey, args.orgId)) return true;
  return sameCncAwardAmount(args.existing.amount, args.amount);
}

/** Keep an existing award bill in step with a PM-adjusted quote total. */
async function syncAwardBillAmount(
  existing: SupplierBill,
  amount: number | null | undefined,
): Promise<void> {
  if (!existing.id) return;
  if (existing.status === 'paid' || existing.status === 'void') return;
  if (existing.installmentPayments && Object.keys(existing.installmentPayments).length > 0) return;
  const next = Math.round((Number(amount) || 0) * 100) / 100;
  if (next <= 0) return;
  if (Math.round((Number(existing.amount) || 0) * 100) / 100 === next) return;
  await updateSupplierBillDetails({ billId: existing.id, amount: next });
}

export async function recordSupplierBillForRfqAward(
  args: {
    project: RfqProject;
    category: 'timber' | 'cnc' | 'other';
    orgId: string;
    orgName: string;
    /** Submission id. Org key ({@link cncRfqAwardSubmissionKey}) only when the award has no quote file. */
    submissionKey: string;
    amount: number | null | undefined;
    description: string;
    byUserId: string;
    byUserName: string;
    /** Proposal node. Other-materials quotes use `timber` so they net against that leftover. */
    plBucket?: ProjectPLBucket;
  },
  options?: { throwOnError?: boolean },
): Promise<void> {
  try {
    if (!args.project.id) return;
    const existingForKey = await findSupplierBillForRfqAward(args.project.id, args.submissionKey);
    // CNC: look up any RFQ+org bill (server may have written org:{id} first).
    // Reuse only for org-key awards or same-dollar race duplicates — not a
    // different structure quote amount.
    const orgBill = !existingForKey && args.category === 'cnc'
      ? await findSupplierBillForCncOrgAward(args.project.id, args.orgId)
      : null;
    const existing = existingForKey
      || (orgBill
        && shouldReuseCncOrgAwardBill({
          submissionKey: args.submissionKey,
          orgId: args.orgId,
          amount: args.amount,
          existing: orgBill,
        })
        ? orgBill
        : null);
    if (existing) {
      await syncAwardBillAmount(existing, args.amount);
      if (existing.id && args.plBucket && !existing.plBucket) {
        await updateSupplierBillDetails({ billId: existing.id, plBucket: args.plBucket });
      }
      return;
    }
    const plBucket = args.plBucket
      ?? (args.category === 'timber' ? 'timber' : args.category === 'cnc' ? 'production' : undefined);
    await createSupplierBill({
      source: 'rfq_award',
      category: args.category,
      vendorName: args.orgName,
      description: args.description,
      amount: args.amount ?? 0,
      supplierOrgId: args.orgId,
      rfqProjectId: args.project.id,
      rfqSubmissionId: args.submissionKey,
      dealId: args.project.dealId || undefined,
      projectId: args.project.timberProjectId || undefined,
      plBucket,
      byUserId: args.byUserId,
      byUserName: args.byUserName,
    });
  } catch (err) {
    console.warn('[supplierBillIngestion] failed to record awarded bill', err);
    if (options?.throwOnError) {
      throw err instanceof Error ? err : new Error('Failed to record supplier bill');
    }
  }
}

/** Void the tracked bill for one awarded submission (timber unapprove/delete). */
export async function retractSupplierBillForRfqAward(args: {
  project: RfqProject;
  submissionKey: string;
  byUserId: string;
  byUserName: string;
}): Promise<void> {
  try {
    if (!args.project.id) return;
    await voidSupplierBillForRfqAward({
      rfqProjectId: args.project.id,
      rfqSubmissionId: args.submissionKey,
      byUserId: args.byUserId,
      byUserName: args.byUserName,
    });
  } catch (err) {
    console.warn('[supplierBillIngestion] failed to retract awarded bill', err);
  }
}

/** Void all tracked bills for a supplier org on an RFQ (CNC unapprove — per-org awards). */
export async function retractSupplierBillsForRfqOrg(args: {
  project: RfqProject;
  orgId: string;
  byUserId: string;
  byUserName: string;
}): Promise<void> {
  try {
    if (!args.project.id) return;
    await voidSupplierBillsForRfqOrg({
      rfqProjectId: args.project.id,
      supplierOrgId: args.orgId,
      byUserId: args.byUserId,
      byUserName: args.byUserName,
    });
  } catch (err) {
    console.warn('[supplierBillIngestion] failed to retract org bills', err);
  }
}
